<template>
    <div class="quote-generator-embedded">
        <div class="max-w-3xl mx-auto">
            <!-- Header -->
            <div class="text-center mb-8">
                <h3 class="text-3xl font-bold mb-2 text-gray-800">
                    {{ t('quoteGenerator.title') }}
                </h3>
                <p class="text-lg text-gray-600">
                    {{ t('quoteGenerator.subtitle') }}
                </p>
                <div class="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-2xl mx-auto">
                    <p class="text-sm text-yellow-800">
                        <strong>{{ t('quoteGenerator.pricingNote.title') }}</strong> {{ t('quoteGenerator.pricingNote.text') }}
                    </p>
                </div>
            </div>

            <!-- Progress Bar -->
            <div class="mb-6">
                <div class="flex justify-between items-center mb-2">
                    <span class="text-sm text-gray-600">{{ t('quoteGenerator.step') }} {{ currentStep }} / {{ totalSteps }}</span>
                    <span class="text-sm text-gray-600">{{ Math.round((currentStep / totalSteps) * 100) }}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                    <div 
                        class="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        :style="{ width: `${(currentStep / totalSteps) * 100}%` }"
                    ></div>
                </div>
            </div>

            <!-- Form Card -->
            <div class="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                <!-- Step 1: Tipo di sito -->
                <div v-show="currentStep === 1" class="space-y-6">
                    <h3 class="text-2xl font-bold text-gray-800 mb-2">{{ t('quoteGenerator.step1.title') }}</h3>
                    <p class="text-sm text-gray-500 mb-6">{{ t('quoteGenerator.microText') }}</p>
                    
                    <div class="space-y-3">
                        <button
                            v-for="option in siteTypes"
                            :key="option.value"
                            @click="selectSiteType(option.value)"
                            :class="[
                                'w-full p-4 text-left rounded-lg border-2 transition-all duration-200',
                                formData.siteType === option.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            ]"
                        >
                            <div class="flex justify-between items-center">
                                <span class="font-medium text-gray-800">{{ option.label }}</span>
                            </div>
                        </button>
                    </div>
                </div>

                <!-- Step 2: Pagine richieste -->
                <div v-show="currentStep === 2" class="space-y-6">
                    <h3 class="text-2xl font-bold text-gray-800 mb-2">{{ t('quoteGenerator.step2.title') }}</h3>
                    <p class="text-sm text-gray-500 mb-6">{{ t('quoteGenerator.microText') }}</p>
                    
                    <div v-if="formData.siteType === 'onepager'" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p class="text-sm text-blue-800">{{ t('quoteGenerator.step2.onepagerNote') }}</p>
                    </div>
                    
                    <div class="space-y-3">
                        <button
                            v-for="option in pageOptions"
                            :key="option.value"
                            @click="formData.pages = option.value"
                            :disabled="formData.siteType === 'onepager'"
                            :class="[
                                'w-full p-4 text-left rounded-lg border-2 transition-all duration-200',
                                formData.pages === option.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : formData.siteType === 'onepager'
                                    ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            ]"
                        >
                            <span class="font-medium text-gray-800">{{ option.label }}</span>
                        </button>
                    </div>
                </div>

                <!-- Step 3: Funzionalità extra -->
                <div v-show="currentStep === 3" class="space-y-6">
                    <h3 class="text-2xl font-bold text-gray-800 mb-2">{{ t('quoteGenerator.step3.title') }}</h3>
                    <p class="text-sm text-gray-500 mb-6">{{ t('quoteGenerator.microText') }}</p>
                    
                    <div class="space-y-3">
                        <label
                            v-for="feature in features"
                            :key="feature.value"
                            :class="[
                                'flex items-center p-4 rounded-lg border-2 transition-all duration-200',
                                feature.free
                                    ? 'border-gray-200 bg-gray-50 opacity-75 cursor-default'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 cursor-pointer'
                            ]"
                        >
                            <input
                                type="checkbox"
                                :value="feature.value"
                                v-model="formData.features"
                                :disabled="feature.free"
                                :checked="feature.free"
                                class="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                            />
                            <span :class="[
                                'ml-3 font-medium',
                                feature.free ? 'text-gray-500 line-through' : 'text-gray-800'
                            ]">
                                {{ feature.label }}
                            </span>
                            <span v-if="feature.free" class="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                {{ t('quoteGenerator.free') }}
                            </span>
                            <span v-else-if="feature.price" class="ml-auto text-sm font-semibold text-gray-700">
                                {{ feature.price }}
                            </span>
                        </label>
                    </div>

                    <div v-if="formData.features.includes('multilang')" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <label for="additionalLanguagesEmbedded" class="block text-sm font-medium text-blue-900 mb-2">
                            Lingue aggiuntive oltre IT/DE/EN
                        </label>
                        <select
                            id="additionalLanguagesEmbedded"
                            v-model.number="formData.additionalLanguages"
                            class="w-full px-3 py-2 rounded-lg border border-blue-300 bg-white text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        >
                            <option :value="0">Nessuna (+ CHF 0)</option>
                            <option :value="1">1 lingua (+ CHF 50)</option>
                            <option :value="2">2 lingue (+ CHF 100)</option>
                            <option :value="3">3 lingue (+ CHF 150)</option>
                            <option :value="4">4 lingue (+ CHF 200)</option>
                        </select>
                    </div>
                </div>

                <!-- Step 4: Design -->
                <div v-show="currentStep === 4" class="space-y-6">
                    <h3 class="text-2xl font-bold text-gray-800 mb-2">{{ t('quoteGenerator.step4.title') }}</h3>
                    <p class="text-sm text-gray-500 mb-6">{{ t('quoteGenerator.microText') }}</p>
                    
                    <div class="space-y-3">
                        <button
                            v-for="option in designOptions"
                            :key="option.value"
                            @click="formData.design = option.value"
                            :class="[
                                'w-full p-4 text-left rounded-lg border-2 transition-all duration-200',
                                formData.design === option.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            ]"
                        >
                            <div class="flex justify-between items-center">
                                <span class="font-medium text-gray-800">{{ option.label }}</span>
                                <span v-if="option.price" class="text-sm font-semibold text-gray-700">
                                    {{ option.price }}
                                </span>
                            </div>
                        </button>
                    </div>
                </div>

                <!-- Step 5: Tempi di consegna -->
                <div v-show="currentStep === 5" class="space-y-6">
                    <h3 class="text-2xl font-bold text-gray-800 mb-2">{{ t('quoteGenerator.step5.title') }}</h3>
                    <p class="text-sm text-gray-500 mb-6">{{ t('quoteGenerator.microText') }}</p>
                    
                    <div class="space-y-3">
                        <button
                            v-for="option in deliveryOptions"
                            :key="option.value"
                            @click="formData.delivery = option.value"
                            :class="[
                                'w-full p-4 text-left rounded-lg border-2 transition-all duration-200',
                                formData.delivery === option.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            ]"
                        >
                            <div class="flex justify-between items-center">
                                <span class="font-medium text-gray-800">{{ option.label }}</span>
                                <span v-if="option.price" class="text-sm font-semibold text-gray-700">
                                    {{ option.price }}
                                </span>
                            </div>
                        </button>
                    </div>
                </div>

                <!-- Step 6: Richiesta Email -->
                <div v-show="currentStep === 6" class="space-y-6">
                    <div class="text-center mb-8">
                        <div class="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                            <svg class="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 class="text-2xl font-bold text-gray-800 mb-2">{{ t('quoteGenerator.email.title') }}</h3>
                        <p class="text-gray-600 mb-8">{{ t('quoteGenerator.email.description') }}</p>
                    </div>

                    <div class="space-y-4">
                        <div>
                            <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                                {{ t('quoteGenerator.email.label') }}
                            </label>
                            <input
                                id="email"
                                type="email"
                                v-model="userEmail"
                                :disabled="emailIsSubmitting"
                                placeholder="nome@esempio.com"
                                class="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                required
                            />
                        </div>

                        <div v-if="emailSubmitStatus === 'success'" class="bg-green-50 border border-green-200 rounded-lg p-4">
                            <p class="text-sm text-green-800 text-center">
                                {{ t('quoteGenerator.email.success') }}
                            </p>
                        </div>

                        <div v-if="emailSubmitStatus === 'error'" class="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p class="text-sm text-red-800 text-center">
                                {{ t('quoteGenerator.email.error') }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Step 7: Risultato e Riepilogo -->
                <div v-show="currentStep === 7" class="space-y-6">
                    <div class="text-center mb-8">
                        <div class="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                            <Check class="h-8 w-8 text-blue-600" />
                        </div>
                        <h3 class="text-2xl font-bold text-gray-800 mb-2">{{ t('quoteGenerator.result.title') }}</h3>
                        <div class="text-4xl font-bold text-blue-600 my-6">
                            {{ priceRange }}
                        </div>
                        <p class="text-gray-600 mb-8">{{ t('quoteGenerator.result.description') }}</p>
                    </div>

                    <!-- Riepilogo Dettagliato -->
                    <div class="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
                        <div class="flex justify-between items-center mb-4">
                            <h4 class="font-bold text-lg text-gray-800">{{ t('quoteGenerator.summary.title') }}</h4>
                            <button
                                @click="editAnswers"
                                class="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 transition-colors"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {{ t('quoteGenerator.summary.edit') }}
                            </button>
                        </div>
                        <div class="space-y-3">
                            <div v-for="(item) in costBreakdown" :key="item.label" class="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                                <span :class="item.free ? 'text-gray-500 line-through' : 'text-gray-700'">{{ item.label }}</span>
                                <span :class="item.free ? 'text-blue-600 font-semibold' : 'font-semibold text-gray-800'">{{ item.price }}</span>
                            </div>
                            <div class="flex justify-between items-center pt-3 mt-3 border-t-2 border-blue-500">
                                <span class="font-bold text-lg text-gray-800">{{ t('quoteGenerator.summary.total') }}</span>
                                <span class="font-bold text-xl text-blue-600">{{ priceRange }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <p class="text-sm text-blue-800 text-center">
                            {{ t('quoteGenerator.result.upgrade') }}
                        </p>
                    </div>
                </div>

                <!-- Navigation Buttons -->
                <div class="flex justify-between mt-8 pt-6 border-t border-gray-200">
                    <button
                        v-if="currentStep > 1 && currentStep < 7"
                        @click="previousStep"
                        class="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        ← {{ t('quoteGenerator.back') }}
                    </button>
                    <div v-else></div>
                    
                    <button
                        v-if="currentStep < 5"
                        @click="nextStep"
                        :disabled="!canProceed"
                        :class="[
                            'px-8 py-3 rounded-lg font-semibold transition-all duration-200',
                            canProceed
                                ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        ]"
                    >
                        {{ t('quoteGenerator.next') }} →
                    </button>
                    
                    <button
                        v-if="currentStep === 5"
                        @click="calculatePrice"
                        :disabled="!canProceed"
                        :class="[
                            'px-8 py-3 rounded-lg font-semibold transition-all duration-200',
                            canProceed
                                ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        ]"
                    >
                        {{ t('quoteGenerator.calculate') }} →
                    </button>

                    <button
                        v-if="currentStep === 6"
                        @click="submitEmail"
                        :disabled="!userEmail || emailIsSubmitting || !isValidEmail(userEmail)"
                        :class="[
                            'px-8 py-3 rounded-lg font-semibold transition-all duration-200',
                            userEmail && isValidEmail(userEmail) && !emailIsSubmitting
                                ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        ]"
                    >
                        <span v-if="emailIsSubmitting">{{ t('quoteGenerator.email.sending') }}...</span>
                        <span v-else>{{ t('quoteGenerator.email.submit') }} →</span>
                    </button>

                    <button
                        v-if="currentStep === 7"
                        @click="completeQuote"
                        class="px-8 py-3 rounded-lg font-semibold transition-all duration-200 bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl"
                    >
                        {{ t('quoteGenerator.continue') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check } from 'lucide-vue-next'

const emit = defineEmits(['quote-complete'])

const { t } = useI18n()

const currentStep = ref(1)
const totalSteps = 7
const userEmail = ref('')
const emailIsSubmitting = ref(false)
const emailSubmitStatus = ref(null)

// Email validation function
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

// Check for saved email in localStorage
const checkSavedEmail = () => {
    const savedEmail = localStorage.getItem('quoteGeneratorEmail')
    if (savedEmail && isValidEmail(savedEmail)) {
        userEmail.value = savedEmail
        return true
    }
    return false
}

const formData = ref({
    siteType: '',
    pages: '',
    features: ['seo'], // SEO is always included for free
    additionalLanguages: 0,
    design: '',
    delivery: ''
})

// Step 1 Options
const siteTypes = computed(() => [
    { value: 'onepager', label: t('quoteGenerator.step1.options.onepager'), price: 'da CHF 400' },
    { value: 'showcase', label: t('quoteGenerator.step1.options.showcase'), price: 'da CHF 799' },
    { value: 'business', label: t('quoteGenerator.step1.options.business'), price: 'da CHF 1\'499' },
    { value: 'ecommerce', label: t('quoteGenerator.step1.options.ecommerce'), price: 'da CHF 1\'999' },
    { value: 'custom', label: t('quoteGenerator.step1.options.custom'), price: 'da CHF 1\'000' }
])

// Step 2 Options
const pageOptions = computed(() => [
    { value: '4-7', label: t('quoteGenerator.step2.options.range2') },
    { value: '8-12', label: t('quoteGenerator.step2.options.range3') },
    { value: '12+', label: t('quoteGenerator.step2.options.range4') }
])

// Step 3 Options
const features = computed(() => [
    { value: 'seo', label: t('quoteGenerator.step3.options.seo'), free: true },
    { value: 'forms', label: t('quoteGenerator.step3.options.forms'), free: true },
    { value: 'multilang', label: t('quoteGenerator.step3.options.multilang'), price: '+ CHF 200 + CHF 50/lingua extra' },
    { value: 'blog', label: t('quoteGenerator.step3.options.blog'), price: '+ CHF 450' },
    { value: 'chatbot', label: t('quoteGenerator.step3.options.chatbot'), price: '+ CHF 500 + 300 CHF/mese' },
    { value: 'cms', label: t('quoteGenerator.step3.options.cms'), price: '+ CHF 400-600' },
    { value: 'booking', label: t('quoteGenerator.step3.options.booking'), price: '+ CHF 100' },
    { value: 'ecommerce', label: t('quoteGenerator.step3.options.ecommerce') }
])

// Step 4 Options
const designOptions = computed(() => {
    return [
        { value: 'similar', label: t('quoteGenerator.step4.options.similar'), price: '+ CHF 50' },
        { value: 'unique', label: t('quoteGenerator.step4.options.unique'), price: '+ CHF 100' }
    ]
})

// Step 5 Options
const deliveryOptions = computed(() => [
    { value: 'standard', label: t('quoteGenerator.step5.options.standard'), price: 'Nessun costo aggiuntivo' },
    { value: 'fast', label: t('quoteGenerator.step5.options.fast'), price: '+ CHF 100' },
    { value: 'lowcost', label: t('quoteGenerator.step5.options.lowcost'), price: '- CHF 100' }
])

// Price calculation with detailed breakdown
const calculatePrices = () => {
    // Calculate prices when we have all form data (step 6 or 7)
    if (currentStep.value < 6) return { base: 0, pages: 0, features: 0, design: 0, delivery: 0, total: 0 }
    
    let basePrice = 0
    let pagesPrice = 0
    let featuresPrice = 0
    let designPrice = 0

    // Base price by site type
    switch (formData.value.siteType) {
        case 'onepager':
            basePrice = 400
            break
        case 'showcase':
            basePrice = 799
            break
        case 'business':
            basePrice = 1499
            break
        case 'ecommerce':
            basePrice = 1999
            break
        case 'custom':
            basePrice = 1000
            break
    }

    // Pages are already included in base price for showcase and business
    // No additional pages price needed
    pagesPrice = 0

    // Features are calculated individually in costBreakdown
    // This is just the total for calculation
    const featurePrices = {
        'multilang': 200 + (formData.value.additionalLanguages * 50),
        'chatbot': 500,
        'ecommerce': formData.value.siteType !== 'ecommerce' ? 2000 : 0,
        'forms': 0, // Free
        'blog': 450,
        'cms': 500,
        'booking': 100,
        'seo': 0 // Free
    }
    
    formData.value.features.forEach(feature => {
        if (featurePrices[feature] !== undefined) {
            featuresPrice += featurePrices[feature]
        }
    })

    // Design (fixed prices)
    switch (formData.value.design) {
        case 'similar':
            designPrice = 50
            break
        case 'unique':
            designPrice = 100
            break
        default:
            designPrice = 0
            break
    }

    // Delivery (fixed prices)
    let deliveryExtra = 0
    if (formData.value.delivery === 'fast') {
        deliveryExtra = 100
    } else if (formData.value.delivery === 'lowcost') {
        deliveryExtra = -100
    }

    const subtotal = basePrice + pagesPrice + featuresPrice + designPrice
    const total = subtotal + deliveryExtra

    return {
        base: basePrice,
        pages: pagesPrice,
        features: featuresPrice,
        design: designPrice,
        delivery: deliveryExtra,
        total: total
    }
}

const prices = computed(() => calculatePrices())

const priceRange = computed(() => {
    const total = prices.value.total
    // Range più stretto: ±15% invece di range ampio
    const min = Math.round(total * 0.85)
    const max = Math.round(total * 1.15)
    return `CHF ${min.toLocaleString('it-CH')} - ${max.toLocaleString('it-CH')}`
})

const costBreakdown = computed(() => {
    const p = prices.value
    const breakdown = []
    
    // Base site
    const siteTypeLabels = {
        'onepager': t('quoteGenerator.summary.baseOnepager'),
        'showcase': t('quoteGenerator.summary.baseShowcase'),
        'business': t('quoteGenerator.summary.baseBusiness'),
        'ecommerce': t('quoteGenerator.summary.baseEcommerce'),
        'custom': t('quoteGenerator.summary.baseCustom')
    }
    breakdown.push({
        label: siteTypeLabels[formData.value.siteType] || '',
        price: `CHF ${p.base.toLocaleString('it-CH')}`
    })
    
    // Pages
    if (p.pages > 0) {
        breakdown.push({
            label: t('quoteGenerator.summary.pages'),
            price: `+ CHF ${p.pages.toLocaleString('it-CH')}`
        })
    }
    
    // Features - show each separately
    const featureLabels = {
        'seo': t('quoteGenerator.step3.options.seo'),
        'multilang': t('quoteGenerator.step3.options.multilang'),
        'forms': t('quoteGenerator.step3.options.forms'),
        'blog': t('quoteGenerator.step3.options.blog'),
        'chatbot': t('quoteGenerator.step3.options.chatbot'),
        'cms': t('quoteGenerator.step3.options.cms'),
        'booking': t('quoteGenerator.step3.options.booking'),
        'ecommerce': t('quoteGenerator.step3.options.ecommerce')
    }
    
    const featurePrices = {
        'seo': 0,
        'multilang': 200 + (formData.value.additionalLanguages * 50),
        'chatbot': 500,
        'ecommerce': formData.value.siteType !== 'ecommerce' ? 2000 : 0,
        'forms': 0,
        'blog': 450,
        'cms': 500,
        'booking': 100
    }
    
    // Always show SEO and Forms as free (if selected)
    if (formData.value.features.includes('seo')) {
    breakdown.push({
        label: featureLabels['seo'],
        price: t('quoteGenerator.summary.free'),
        free: true
    })
    }
    if (formData.value.features.includes('forms')) {
        breakdown.push({
            label: featureLabels['forms'],
            price: t('quoteGenerator.summary.free'),
            free: true
        })
    }
    
    // Show other selected features (paid ones)
    formData.value.features.forEach(feature => {
        if (feature !== 'seo' && feature !== 'forms' && featurePrices[feature] !== undefined && featurePrices[feature] > 0) {
            const isMultilang = feature === 'multilang'
            const multilangText = formData.value.additionalLanguages > 0
                ? `+ CHF 200 + ${formData.value.additionalLanguages} x CHF 50`
                : '+ CHF 200'
            const isCms = feature === 'cms'
            breakdown.push({
                label: featureLabels[feature] || feature,
                price: isMultilang
                    ? multilangText
                    : isCms
                    ? '+ CHF 400-600'
                    : `+ CHF ${featurePrices[feature].toLocaleString('it-CH')}`
            })
        }
    })
    
    // Design
    if (p.design > 0) {
        const designLabels = {
            'similar': t('quoteGenerator.summary.designSimilar'),
            'unique': t('quoteGenerator.summary.designUnique')
        }
        breakdown.push({
            label: designLabels[formData.value.design] || t('quoteGenerator.summary.design'),
            price: `+ CHF ${p.design.toLocaleString('it-CH')}`
        })
    }
    
    // Delivery
    if (p.delivery !== 0) {
        const deliveryLabels = {
            'fast': t('quoteGenerator.summary.deliveryFast'),
            'lowcost': t('quoteGenerator.summary.deliveryLowcost')
        }
        breakdown.push({
            label: deliveryLabels[formData.value.delivery] || '',
            price: p.delivery > 0 ? `+ CHF ${p.delivery.toLocaleString('it-CH')}` : `- CHF ${Math.abs(p.delivery).toLocaleString('it-CH')}`
        })
    }
    
    return breakdown
})

const canProceed = computed(() => {
    switch (currentStep.value) {
        case 1:
            return formData.value.siteType !== ''
        case 2:
            // For one pager, pages are not required
            return formData.value.siteType === 'onepager' || formData.value.pages !== ''
        case 3:
            return true // Features are optional
        case 4:
            return formData.value.design !== ''
        case 5:
            return formData.value.delivery !== ''
        default:
            return true
    }
})

const selectSiteType = (value) => {
    formData.value.siteType = value
    // Always add SEO and Forms (they're free)
    if (!formData.value.features.includes('seo')) {
        formData.value.features.push('seo')
    }
    if (!formData.value.features.includes('forms')) {
        formData.value.features.push('forms')
    }
}

const nextStep = () => {
    if (canProceed.value && currentStep.value < totalSteps) {
        // Skip step 2 if one pager is selected
        if (currentStep.value === 1 && formData.value.siteType === 'onepager') {
            formData.value.pages = 'onepager' // Set default for one pager
            currentStep.value = 3 // Skip to features
        } else {
            currentStep.value++
        }
    }
}

const previousStep = () => {
    if (currentStep.value > 1) {
        // Skip step 2 if one pager is selected
        if (currentStep.value === 3 && formData.value.siteType === 'onepager') {
            currentStep.value = 1 // Go back to step 1
        } else if (currentStep.value === 7) {
            // If going back from results, go to email step
            currentStep.value = 6
        } else {
            currentStep.value--
        }
    }
}

const calculatePrice = () => {
    if (canProceed.value) {
        // Check if email is already saved
        if (checkSavedEmail()) {
            // Email exists, submit it automatically and go to results
            submitEmail(true) // Pass true to skip showing the form
        } else {
            // No saved email, show email step
        currentStep.value = 6
        }
    }
}

const submitEmail = async (skipForm = false) => {
    if (!userEmail.value || !isValidEmail(userEmail.value)) {
        return
    }

    emailIsSubmitting.value = true
    if (!skipForm) {
        emailSubmitStatus.value = null
    }

    try {
        // Send ONLY email data
        const emailData = {
            tracking_code: 'AVElLWEEFS1qC3341qy6mQ',
            url: window.location.href,
            form_name: 'Quote Generator Embedded Email Unlock',
            form_id: 'quote-generator-embedded-email-unlock',
            form_action: window.location.href,
            form_data: {
                email: userEmail.value
            },
            field_names: ['email'],
            referrer: document.referrer || null,
            user_agent: navigator.userAgent || null
        }

        // Use sendBeacon for reliability
        if (navigator.sendBeacon) {
            navigator.sendBeacon(
                'https://panettone-zeta.vercel.app/analytics/api/c',
                new Blob([JSON.stringify(emailData)], { type: 'application/json' })
            )
            // Save email to localStorage
            localStorage.setItem('quoteGeneratorEmail', userEmail.value)
            if (!skipForm) {
                emailSubmitStatus.value = 'success'
            }
            // Move to results step after successful email submission
            setTimeout(() => {
                currentStep.value = 7
                // Send quote results as separate form
                sendQuoteResults()
            }, skipForm ? 0 : 1000)
        } else {
            const response = await fetch('https://panettone-zeta.vercel.app/analytics/api/c', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(emailData),
                keepalive: true
            })

            if (response.ok) {
                // Save email to localStorage
                localStorage.setItem('quoteGeneratorEmail', userEmail.value)
                if (!skipForm) {
                    emailSubmitStatus.value = 'success'
                }
                // Move to results step after successful email submission
                setTimeout(() => {
                    currentStep.value = 7
                    // Send quote results as separate form
                    sendQuoteResults()
                }, skipForm ? 0 : 1000)
            } else {
                throw new Error('Email submission failed')
            }
        }
    } catch (error) {
        emailSubmitStatus.value = 'error'
        console.error('Email submission error:', error)
    } finally {
        emailIsSubmitting.value = false
    }
}

const sendQuoteResults = () => {
    try {
        // Send quote results as separate form
        const quoteResultsData = {
            tracking_code: 'AVElLWEEFS1qC3341qy6mQ',
            url: window.location.href,
            form_name: 'Quote Generator Embedded Results',
            form_id: 'quote-generator-embedded-results',
            form_action: window.location.href,
            form_data: {
                email: userEmail.value,
                siteType: formData.value.siteType,
                pages: formData.value.pages,
                features: formData.value.features,
                design: formData.value.design,
                delivery: formData.value.delivery,
                priceRange: priceRange.value,
                totalPrice: prices.value.total,
                basePrice: prices.value.base,
                featuresPrice: prices.value.features,
                designPrice: prices.value.design,
                deliveryPrice: prices.value.delivery,
                costBreakdown: costBreakdown.value.map(item => ({
                    label: item.label,
                    price: item.price,
                    free: item.free || false
                }))
            },
            field_names: ['email', 'siteType', 'pages', 'features', 'design', 'delivery', 'priceRange', 'totalPrice'],
            referrer: document.referrer || null,
            user_agent: navigator.userAgent || null
        }

        // Use sendBeacon for reliability
        if (navigator.sendBeacon) {
            navigator.sendBeacon(
                'https://panettone-zeta.vercel.app/analytics/api/c',
                new Blob([JSON.stringify(quoteResultsData)], { type: 'application/json' })
            )
        } else {
            fetch('https://panettone-zeta.vercel.app/analytics/api/c', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(quoteResultsData),
                keepalive: true
            }).catch(() => {})
        }
    } catch (error) {
        // Silently fail - analytics should not break the user experience
        console.error('Quote results submission error:', error)
    }
}


const editAnswers = () => {
    // Go back to step 1 to allow editing
    currentStep.value = 1
    // Scroll to top of the section
    setTimeout(() => {
        const section = document.querySelector('.quote-generator-embedded')
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, 100)
}

const completeQuote = () => {
    // Emit event with quote data
    emit('quote-complete', {
        ...formData.value,
        priceRange: priceRange.value,
        costBreakdown: costBreakdown.value,
        totalPrice: prices.value.total
    })
}
</script>

<style scoped>
.quote-generator-embedded {
    width: 100%;
}
</style>


